import {ApiTarget} from 'Types/api';
import type {RequestOptions} from 'Types/api';
import type {
  IncentiveTierCheck,
  IncentiveTiersResponse,
} from 'Types/incentiveTier/incentiveTierTypes';
import api, {HttpStatusCodes} from 'Utility/api';
import {camelizeKeys} from 'Utility/commons/transformKeys';

export const getIncentiveTiers = async (
  dealId: string,
  active = false
): Promise<IncentiveTiersResponse> => {
  const payload: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const options: RequestOptions = {
    anonymous: true,
  };

  try {
    const response = await api.getJson<IncentiveTiersResponse>(
      ApiTarget.V1,
      `deals/${dealId}/incentive_tiers?active=${active}`,
      payload,
      options
    );

    return camelizeKeys(response);
  } catch (error) {
    throw new Error('Something went wrong fetching incentive tiers.');
  }
};

export const getInvestorIncentiveTiers = async (
  dealId: number,
  investorId: number
): Promise<IncentiveTiersResponse> => {
  const payload: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = await api.getJson<IncentiveTiersResponse>(
      ApiTarget.V1,
      `deals/${dealId}/investors/${investorId}/incentive_tiers`,
      payload
    );

    return camelizeKeys(response);
  } catch (error) {
    throw new Error('Something went wrong fetching incentive tiers.');
  }
};

const defaultTierCheck: IncentiveTierCheck = {
  success: false,
  message: 'Something went wrong finding the promo code.',
};

export const getIncentiveTierByCode = async (
  dealId: string,
  promoCode: string,
  referrerEmail: string,
  jwtToken: string
): Promise<IncentiveTierCheck> => {
  try {
    const whitelistedDomain = document.location.href;

    const init: RequestInit = {
      headers: {
        'X-DEALMAKER-DOMAIN': whitelistedDomain,
      },
    };

    const params = new URLSearchParams({
      code: promoCode,
      referrer_email: referrerEmail,
      token: jwtToken,
    });

    const response = await api.get(
      ApiTarget.EmbedV1,
      `deals/${dealId}/investors/free_shares_codes?${params}`,
      init,
      {anonymous: true}
    );

    if (response?.ok || response?.status === HttpStatusCodes.NOT_FOUND) {
      const body = await response.json();
      return camelizeKeys<IncentiveTierCheck>(body);
    }

    return defaultTierCheck;
  } catch (error) {
    console.error('DealMaker error applying promo code:', error);
    return defaultTierCheck;
  }
};
